/**
 * Der Kernscreen: Belegprüfung im Split View.
 *
 * Hier verbringt der Beirat neunzig Prozent seiner Zeit. Alles andere darf
 * funktional sein; dieser Screen muss schnell sein und vollständig über die
 * Tastatur bedienbar — wer 187 Belege durchsieht, klickt nicht.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PdfAnsicht } from '../komponenten/PdfAnsicht';
import { BelegBlatt } from '../komponenten/BelegBlatt';
import { DEMO } from '../demo/modus';
import { api, datumKurz, datum, eur } from '../lib/api';
import { Haken } from '../komponenten/Haken';
import type {
  Banktransaktion,
  Beleg,
  Fundstelle,
  Gruppe,
  PdfRechteck,
  PruefStatus,
  Zahlungsbefund,
} from '../lib/typen';

type Filter = 'alle' | 'offen' | 'rueckfrage' | 'gross';

export interface BelegpruefungProps {
  pruefungId: string;
  belege: Beleg[];
  gliederung: Gruppe[];
  aktuellerBeleg: string | null;
  onBelegWechsel: (belegId: string) => void;
  onStatus: (belegId: string, status: PruefStatus) => void;
  onRueckfrage: (belegId: string, markierung?: PdfRechteck) => void;
  onHilfe: () => void;
  /** Vorfilter auf ein Sachkonto. */
  vorfilter?: string | null;
  /** Zahlungsbefund je Beleg — speist das Zahlungsfeld rechts. */
  zahlungen: Record<string, Zahlungsbefund>;
  transaktionen: Banktransaktion[];
  /** Sprung in den Kontoauszug, auf genau diese Position. */
  onPosition: (transaktionId: string) => void;
  /** Sprung in das hochgeladene Kontoauszug-PDF, an die markierte Stelle. */
  onDokumentStelle?: (dokumentId: string, stelle: PdfRechteck) => void;
  /** Den Beleg im Kontoauszug-PDF suchen, wenn der Abgleich nichts hat. */
  onFundstelle?: (belegId: string) => Promise<Fundstelle | null>;
  /** Den Beleg mit dem Sprachmodell im Auszug suchen; liefert true, wenn etwas zugeordnet wurde. */
  onKiSuche?: (belegId: string) => Promise<boolean>;
  /** Das Belegbild aufrichten. Gilt für alle Prüfer, nicht nur für diesen. */
  onDrehen: (belegId: string, grad: 0 | 90 | 180 | 270) => void;
}

export function Belegpruefung({
  pruefungId,
  belege,
  gliederung,
  aktuellerBeleg,
  onBelegWechsel,
  onStatus,
  onRueckfrage,
  onHilfe,
  vorfilter,
  zahlungen,
  transaktionen,
  onPosition,
  onDokumentStelle,
  onFundstelle,
  onKiSuche,
  onDrehen,
}: BelegpruefungProps) {
  const [filter, setFilter] = useState<Filter>('alle');
  const [suche, setSuche] = useState(vorfilter ?? '');
  const [offeneGruppen, setOffeneGruppen] = useState<Set<number>>(new Set());
  const [hervorhebungen, setHervorhebungen] = useState<Record<string, PdfRechteck>>({});
  const [aktivesFeld, setAktivesFeld] = useState<string | null>(null);
  const [baumSichtbar, setBaumSichtbar] = useState(true);
  /** Rückfrage „Sind Sie sicher?" vor dem Abhaken einer ganzen Gruppe. */
  const [sammelFrage, setSammelFrage] = useState<{ gruppe: Gruppe; offen: Beleg[]; rueckfragen: number } | null>(null);
  const [sammelLaeuft, setSammelLaeuft] = useState(false);
  const suchfeld = useRef<HTMLInputElement>(null);

  const belegNach = useMemo(() => new Map(belege.map((b) => [b.id, b])), [belege]);

  useEffect(() => {
    if (vorfilter) {
      setSuche(vorfilter);
      setFilter('alle');
    }
  }, [vorfilter]);

  // ── Filterung ────────────────────────────────────────────
  const sichtbar = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return belege.filter((b) => {
      if (filter === 'offen' && b.pruefstatus !== 'offen') return false;
      if (filter === 'rueckfrage' && b.pruefstatus !== 'rueckfrage') return false;
      if (filter === 'gross' && (b.brutto ?? 0) <= 1000) return false;
      if (!q) return true;
      return (
        (b.lieferant ?? '').toLowerCase().includes(q) ||
        (b.rechnungsnummer ?? '').toLowerCase().includes(q) ||
        String(b.brutto ?? '').includes(q) ||
        b.buchungszeilen.some((z) => (z.sachkontoNummer ?? '').includes(q))
      );
    });
  }, [belege, filter, suche]);

  const sichtbarIds = useMemo(() => new Set(sichtbar.map((b) => b.id)), [sichtbar]);

  /** Die flache Reihenfolge, in der J und K durchlaufen — wie im Baum. */
  const reihenfolge = useMemo(() => {
    const ids: string[] = [];
    for (const gruppe of gliederung) {
      for (const id of gruppe.belegIds) {
        if (sichtbarIds.has(id) && !ids.includes(id)) ids.push(id);
      }
    }
    // Belege ohne Gruppenzuordnung hängen hinten an, damit keiner verloren geht.
    for (const b of sichtbar) if (!ids.includes(b.id)) ids.push(b.id);
    return ids;
  }, [gliederung, sichtbarIds, sichtbar]);

  const beleg = aktuellerBeleg ? belegNach.get(aktuellerBeleg) ?? null : null;
  const index = aktuellerBeleg ? reihenfolge.indexOf(aktuellerBeleg) : -1;

  // Die Gruppe des aktuellen Belegs aufklappen, damit er im Baum sichtbar ist.
  useEffect(() => {
    if (!aktuellerBeleg) return;
    const treffer = gliederung.findIndex((g) => g.belegIds.includes(aktuellerBeleg));
    if (treffer >= 0) setOffeneGruppen((alt) => new Set(alt).add(treffer));
  }, [aktuellerBeleg, gliederung]);

  // ── Hervorhebungen laden ─────────────────────────────────
  useEffect(() => {
    if (!beleg) return;
    let verworfen = false;
    setHervorhebungen({});
    setAktivesFeld(null);
    api
      .hervorhebungen(beleg.id)
      .then((h) => {
        if (!verworfen) setHervorhebungen(h);
      })
      // Ein gescannter Beleg ohne Textebene liefert nichts. Das ist kein
      // Fehler, sondern ein Zustand: Die Hervorhebung entfällt dann still,
      // statt irgendwo daneben zu leuchten.
      .catch(() => undefined);
    return () => {
      verworfen = true;
    };
  }, [beleg?.id]);

  // ── Navigation ───────────────────────────────────────────
  const springen = useCallback(
    (schritte: number) => {
      if (!reihenfolge.length) return;
      const ziel = Math.max(0, Math.min(reihenfolge.length - 1, (index < 0 ? 0 : index) + schritte));
      onBelegWechsel(reihenfolge[ziel]);
    },
    [index, reihenfolge, onBelegWechsel],
  );

  const alsGeprueft = useCallback(() => {
    if (!beleg) return;
    onStatus(beleg.id, 'geprueft');
    springen(1);
  }, [beleg, onStatus, springen]);

  // ── Tastatur ─────────────────────────────────────────────
  useEffect(() => {
    function taste(e: KeyboardEvent) {
      const ziel = e.target as HTMLElement | null;
      if (ziel && /input|textarea|select/i.test(ziel.tagName)) {
        if (e.key === 'Escape') ziel.blur();
        return;
      }
      switch (e.key.toLowerCase()) {
        case 'j':
        case 'arrowdown':
          e.preventDefault();
          springen(1);
          break;
        case 'k':
        case 'arrowup':
          e.preventDefault();
          springen(-1);
          break;
        case 'g':
          e.preventDefault();
          alsGeprueft();
          break;
        case 'r':
          e.preventDefault();
          if (beleg) onRueckfrage(beleg.id);
          break;
        case 'f':
          e.preventDefault();
          suchfeld.current?.focus();
          break;
        case '[':
          e.preventDefault();
          setBaumSichtbar((s) => !s);
          break;
        case '?':
          e.preventDefault();
          onHilfe();
          break;
      }
    }
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [springen, alsGeprueft, beleg, onRueckfrage, onHilfe]);

  // ── Baum ─────────────────────────────────────────────────
  const baum = useMemo(() => {
    const zeilen: Array<
      | { art: 'gruppe'; gruppe: Gruppe; nummer: number; anzahl: number; geprueft: number }
      | { art: 'beleg'; beleg: Beleg }
      | { art: 'fuss'; gruppe: Gruppe; nummer: number; summe: number; anzahl: number; offen: Beleg[]; rueckfragen: number }
    > = [];

    gliederung.forEach((gruppe, nummer) => {
      const gruppenBelege = gruppe.belegIds
        .map((id) => belegNach.get(id))
        .filter((b): b is Beleg => Boolean(b) && sichtbarIds.has(b!.id));
      if (!gruppenBelege.length) return;

      zeilen.push({
        art: 'gruppe',
        gruppe,
        nummer,
        anzahl: gruppe.belegIds.length,
        geprueft: gruppenBelege.filter((b) => b.pruefstatus === 'geprueft').length,
      });

      if (offeneGruppen.has(nummer)) {
        for (const b of gruppenBelege) zeilen.push({ art: 'beleg', beleg: b });
        // Am Ende der Liste: die Bruttosumme des Kontos — zum Vergleich mit
        // der Abrechnung — und der Weg, alles auf einmal abzuhaken. Die
        // Summe gilt für das ganze Konto, nicht nur für die Zeilen, die
        // der Filter gerade zeigt.
        const alle = gruppe.belegIds.map((id) => belegNach.get(id)).filter((b): b is Beleg => Boolean(b));
        zeilen.push({
          art: 'fuss',
          gruppe,
          nummer,
          summe: alle.reduce((s, b) => s + (b.brutto ?? 0), 0),
          anzahl: alle.length,
          offen: alle.filter((b) => b.pruefstatus === 'offen'),
          rueckfragen: alle.filter((b) => b.pruefstatus === 'rueckfrage').length,
        });
      }
    });

    return zeilen;
  }, [gliederung, belegNach, sichtbarIds, offeneGruppen]);

  const rechteck = aktivesFeld ? hervorhebungen[aktivesFeld] ?? null : null;

  async function sammelBestaetigen() {
    if (!sammelFrage) return;
    setSammelLaeuft(true);
    try {
      for (const b of sammelFrage.offen) onStatus(b.id, 'geprueft');
    } finally {
      setSammelLaeuft(false);
      setSammelFrage(null);
    }
  }

  return (
    <div className="split">
      {sammelFrage && (
        <div className="ovl" onMouseDown={(e) => e.target === e.currentTarget && setSammelFrage(null)}>
          <div className="sheet" role="alertdialog" aria-labelledby="sammel-titel">
            <h3 id="sammel-titel">Sind Sie sicher?</h3>
            <p className="s">
              {sammelFrage.offen.length} {sammelFrage.offen.length === 1 ? 'offener Beleg' : 'offene Belege'} auf dem Konto{' '}
              <b>
                {sammelFrage.gruppe.ohneKontierung ? '' : `${sammelFrage.gruppe.sachkonto} `}
                {sammelFrage.gruppe.bezeichnung}
              </b>{' '}
              {sammelFrage.offen.length === 1 ? 'wird' : 'werden'} als geprüft markiert — ohne dass Sie jeden einzeln angesehen haben.
              {sammelFrage.rueckfragen > 0 && ` ${sammelFrage.rueckfragen} ${sammelFrage.rueckfragen === 1 ? 'Beleg mit Rückfrage bleibt' : 'Belege mit Rückfrage bleiben'} unverändert.`}
              {' '}Im Protokoll erscheinen diese Belege als geprüft. Sie können einzelne Belege danach wieder auf „offen" setzen.
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
              <button className="btn ghost" onClick={() => setSammelFrage(null)} disabled={sammelLaeuft}>
                Abbrechen
              </button>
              <button className="btn primary" onClick={sammelBestaetigen} disabled={sammelLaeuft} autoFocus>
                {sammelLaeuft ? 'Wird markiert …' : `Ja, ${sammelFrage.offen.length} als geprüft markieren`}
              </button>
            </div>
          </div>
        </div>
      )}
      {baumSichtbar && (
        <aside className="pane pane-tree">
          <div className="pane-head">
            <label className="search">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <input
                ref={suchfeld}
                type="search"
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
                placeholder="Lieferant, Nr., Sachkonto …"
                aria-label="Belege durchsuchen"
              />
            </label>
            <div className="chips">
              {(
                [
                  ['alle', 'Alle'],
                  ['offen', 'Offen'],
                  ['rueckfrage', 'Rückfragen'],
                  ['gross', '> 1.000 €'],
                ] as [Filter, string][]
              ).map(([wert, beschriftung]) => (
                <button
                  key={wert}
                  className="fchip"
                  aria-pressed={filter === wert}
                  onClick={() => setFilter(wert)}
                >
                  {beschriftung}
                </button>
              ))}
            </div>
          </div>

          <div className="tree">
            {baum.length === 0 ? (
              <div style={{ padding: '24px 14px', textAlign: 'center', color: 'var(--tx-2)', fontSize: 12.5 }}>
                Kein Beleg entspricht diesen Filtern.
                <br />
                <button
                  className="btn ghost sm"
                  style={{ marginTop: 10 }}
                  onClick={() => {
                    setFilter('alle');
                    setSuche('');
                  }}
                >
                  Filter zurücksetzen
                </button>
              </div>
            ) : (
              baum.map((zeile) => {
                if (zeile.art === 'gruppe') {
                  const offen = offeneGruppen.has(zeile.nummer);
                  return (
                    <button
                      key={`g${zeile.nummer}`}
                      className={`tree-gruppe${zeile.gruppe.ohneKontierung ? ' fremd' : ''}`}
                      onClick={() =>
                        setOffeneGruppen((alt) => {
                          const neu = new Set(alt);
                          neu.has(zeile.nummer) ? neu.delete(zeile.nummer) : neu.add(zeile.nummer);
                          return neu;
                        })
                      }
                      aria-expanded={offen}
                    >
                      <span style={{ color: 'var(--tx-3)', fontSize: 10 }}>{offen ? '▾' : '▸'}</span>
                      <span className="nr">
                        {zeile.gruppe.ohneKontierung ? '—' : zeile.gruppe.sachkonto}
                      </span>
                      <span className="bz">{zeile.gruppe.bezeichnung}</span>
                      <span className="ct">
                        {zeile.geprueft}/{zeile.anzahl}
                      </span>
                      <Ring anteil={zeile.anzahl ? zeile.geprueft / zeile.anzahl : 0} />
                    </button>
                  );
                }
                if (zeile.art === 'fuss') {
                  return (
                    <div key={`f${zeile.nummer}`} className="tree-fuss">
                      <div className="tree-fuss-summe">
                        <span>
                          Summe brutto · {zeile.anzahl} {zeile.anzahl === 1 ? 'Beleg' : 'Belege'}
                        </span>
                        <b>{eur(zeile.summe)}</b>
                      </div>
                      {zeile.offen.length > 0 ? (
                        <button
                          className="btn ghost sm tree-fuss-knopf"
                          onClick={() => setSammelFrage({ gruppe: zeile.gruppe, offen: zeile.offen, rueckfragen: zeile.rueckfragen })}
                          title="Alle offenen Belege dieses Kontos als geprüft markieren"
                        >
                          ✓ {zeile.offen.length} {zeile.offen.length === 1 ? 'offenen' : 'offene'} als geprüft markieren
                        </button>
                      ) : (
                        <span className="tree-fuss-fertig">
                          {zeile.rueckfragen ? `Alle geprüft, ${zeile.rueckfragen} mit Rückfrage` : 'Alle Belege geprüft'}
                        </span>
                      )}
                    </div>
                  );
                }
                const b = zeile.beleg;
                return (
                  <button
                    key={b.id}
                    className={`tree-beleg${b.id === aktuellerBeleg ? ' aktiv' : ''}`}
                    onClick={() => onBelegWechsel(b.id)}
                  >
                    <span className={`sd ${b.pruefstatus}`} aria-hidden />
                    <span className="nm">{b.lieferant ?? 'Ohne Lieferant'}</span>
                    <Haken zustand={b.pruefstatus} />
                    <span className="dt">{datumKurz(b.rechnungsdatum)}</span>
                    <span className="amt">
                      {(b.brutto ?? 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </aside>
      )}

      {beleg ? (
        // Im Klickdummy gibt es keine echten Beleg-PDFs. Statt eines leeren
        // grauen Kastens wird der Beleg gezeichnet — sonst zeigt der Dummy
        // ausgerechnet das Feld-Highlighting nicht, auf das es hier ankommt.
        DEMO ? (
          <BelegBlatt
            beleg={beleg}
            aktivesFeld={aktivesFeld}
            onMarkierung={(m) => onRueckfrage(beleg.id, m)}
          />
        ) : (
          <PdfAnsicht
            belegId={beleg.id}
            url={api.belegPdfUrl(beleg.id)}
            hervorhebung={rechteck}
            onMarkierung={(m) => onRueckfrage(beleg.id, m)}
            drehung={beleg.drehung ?? 0}
            onDrehen={(grad) => onDrehen(beleg.id, grad)}
          />
        )
      ) : (
        <div className="pane pane-pdf">
          <div className="leer card" style={{ margin: 'auto' }}>
            <h3>Kein Beleg ausgewählt</h3>
            <p>Wählen Sie links einen Beleg oder drücken Sie J, um zu beginnen.</p>
          </div>
        </div>
      )}

      <aside className="pane pane-book">
        {beleg ? (
          <>
            <div className="book">
              <BelegKopf beleg={beleg} onFeld={setAktivesFeld} />

              <div className="blk">
                <h4>Buchungszeilen</h4>
                <table className="zeilen">
                  <thead>
                    <tr>
                      <th style={{ width: 92 }}>Sachkonto</th>
                      <th>Text</th>
                      <th className="r">Netto</th>
                      <th className="r">USt</th>
                      <th className="r">Brutto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {beleg.buchungszeilen.map((z, i) => (
                      <tr
                        key={z.id}
                        className="hov"
                        tabIndex={0}
                        onMouseEnter={() => setAktivesFeld(`zeile${i}`)}
                        onMouseLeave={() => setAktivesFeld(null)}
                        onFocus={() => setAktivesFeld(`zeile${i}`)}
                        onBlur={() => setAktivesFeld(null)}
                      >
                        <td className="mono kontozelle">
                          {z.sachkontoNummer ?? '—'}
                          <span className="kontoname" title={z.sachkontoBezeichnung ?? ''}>
                            {z.sachkontoBezeichnung ?? ''}
                          </span>
                        </td>
                        <td>{z.text}</td>
                        <td className="r">{z.netto?.toLocaleString('de-DE', { minimumFractionDigits: 2 }) ?? '—'}</td>
                        <td className="r">{z.ustProzent !== null ? `${z.ustProzent} %` : '—'}</td>
                        <td className="r">{z.brutto?.toLocaleString('de-DE', { minimumFractionDigits: 2 }) ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {beleg.buchungszeilen.length > 1 && (
                  <p className="note">
                    Dieser Beleg trifft {new Set(beleg.buchungszeilen.map((z) => z.sachkontoNummer)).size}{' '}
                    Sachkonten.
                  </p>
                )}
                {beleg.buchungszeilen.length === 0 && (
                  <p className="note">
                    Zu diesem Beleg ist in der Buchhaltung keine Buchungszeile hinterlegt.
                  </p>
                )}
              </div>

              <Zahlungsblock
                key={beleg.id}
                belegId={beleg.id}
                befund={zahlungen[beleg.id] ?? null}
                transaktionen={transaktionen}
                onPosition={onPosition}
                onDokumentStelle={onDokumentStelle}
                onFundstelle={onFundstelle}
                onKiSuche={onKiSuche}
              />

              {beleg.buchungszeilen.some((z) => z.kostenstellen.length) && (
                <div className="blk">
                  <h4>Kostenstellen</h4>
                  <dl className="umlage">
                    {beleg.buchungszeilen.flatMap((z) =>
                      z.kostenstellen.map((k) => [
                        <dt key={`${z.id}-${k.nummer}-t`}>
                          {k.nummer} {k.bezeichnung}
                        </dt>,
                        <dd key={`${z.id}-${k.nummer}-d`} className="num">
                          {eur(k.betrag)}
                        </dd>,
                      ]),
                    )}
                  </dl>
                </div>
              )}

              <div className="blk">
                <h4>Tastatur</h4>
                <div style={{ fontSize: 12, color: 'var(--tx-2)', lineHeight: 2 }}>
                  <kbd>J</kbd> weiter · <kbd>K</kbd> zurück · <kbd>G</kbd> geprüft · <kbd>R</kbd>{' '}
                  Rückfrage · <kbd>?</kbd> alle
                </div>
              </div>
            </div>

            <div className="actionbar">
              <button className="btn ok sm" onClick={alsGeprueft}>
                Geprüft
              </button>
              <button className="btn warnline sm" onClick={() => onRueckfrage(beleg.id)}>
                Rückfrage
              </button>
              <button
                className="btn ghost sm"
                style={{ border: 0 }}
                onClick={() => springen(1)}
              >
                Überspringen
              </button>
              <span className="pos">
                {index >= 0 ? index + 1 : '–'} von {reihenfolge.length}
              </span>
              <button className="navbtn" onClick={() => springen(-1)} aria-label="Vorheriger Beleg">
                ↑
              </button>
              <button className="navbtn" onClick={() => springen(1)} aria-label="Nächster Beleg">
                ↓
              </button>
            </div>
          </>
        ) : (
          <div style={{ padding: 20, color: 'var(--tx-2)', fontSize: 13 }}>
            Hier erscheinen die Buchungszeilen des gewählten Belegs.
          </div>
        )}
      </aside>
    </div>
  );
}

/** Kopfdaten des Belegs. Jedes Feld löst beim Überfahren die Hervorhebung aus. */
function BelegKopf({
  beleg,
  onFeld,
}: {
  beleg: Beleg;
  onFeld: (feld: string | null) => void;
}) {
  const felder: [string, string, string][] = [
    ['Rechnungsnummer', 'rnr', beleg.rechnungsnummer ?? '—'],
    ['Rechnungsdatum', 'datum', datum(beleg.rechnungsdatum)],
    ['Fälligkeit', 'faellig', datum(beleg.faelligkeit)],
    ['Netto', 'netto', eur(beleg.netto)],
    ['USt-Satz', 'ust', beleg.ustProzent !== null ? `${beleg.ustProzent} %` : '—'],
    ['Brutto', 'brutto', eur(beleg.brutto)],
  ];

  const status =
    beleg.pruefstatus === 'geprueft'
      ? { klasse: 'ok', text: 'Geprüft' }
      : beleg.pruefstatus === 'rueckfrage'
        ? { klasse: 'w', text: 'Rückfrage' }
        : { klasse: 'n', text: 'Offen' };

  return (
    <div className="belegkopf">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3
            className="hov"
            onMouseEnter={() => onFeld('lieferant')}
            onMouseLeave={() => onFeld(null)}
          >
            {beleg.lieferant ?? 'Ohne Lieferant'}
          </h3>
          <div className="mono" style={{ fontSize: 12, color: 'var(--tx-2)' }}>
            {beleg.buchungszeilen[0]?.sachkontoNummer ?? '—'} ·{' '}
            {beleg.buchungszeilen[0]?.sachkontoBezeichnung ?? 'Ohne Sachkonto'}
          </div>
        </div>
        <span className={`pill ${status.klasse}`}>
          <span className="dot" />
          {status.text}
        </span>
      </div>

      <dl className="dl">
        {felder.map(([beschriftung, feld, wert]) => (
          <div key={feld} style={{ display: 'contents' }}>
            <dt>{beschriftung}</dt>
            <dd
              className={`hov${feld === 'rnr' ? ' mono' : ''}`}
              tabIndex={0}
              onMouseEnter={() => onFeld(feld)}
              onMouseLeave={() => onFeld(null)}
              onFocus={() => onFeld(feld)}
              onBlur={() => onFeld(null)}
              style={feld === 'brutto' ? { fontWeight: 600 } : undefined}
            >
              {wert}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * Der Zahlungsteil der Prüfung.
 *
 * Beide Daten stehen nebeneinander — das Zahldatum der Buchhaltung und das
 * Buchungsdatum im Kontoauszug — statt eines zusammengeführten Wertes. Wer
 * prüft, muss sehen können, woher eine Zahl stammt.
 *
 * Von hier springt der Beirat in den Kontoauszug, auf genau die Position.
 * Bei einer Sammelüberweisung sind es mehrere, deshalb eine Liste.
 */
function Zahlungsblock({
  belegId,
  befund,
  transaktionen,
  onPosition,
  onDokumentStelle,
  onFundstelle,
  onKiSuche,
}: {
  belegId: string;
  befund: Zahlungsbefund | null;
  transaktionen: Banktransaktion[];
  onPosition: (transaktionId: string) => void;
  onDokumentStelle?: (dokumentId: string, stelle: PdfRechteck) => void;
  onFundstelle?: (belegId: string) => Promise<Fundstelle | null>;
  onKiSuche?: (belegId: string) => Promise<boolean>;
}) {
  const [kiStand, setKiStand] = useState<'bereit' | 'sucht' | 'nichts'>('bereit');
  useEffect(() => setKiStand('bereit'), [belegId]);
  async function kiSuchen() {
    if (!onKiSuche) return;
    setKiStand('sucht');
    try {
      const gefunden = await onKiSuche(belegId);
      setKiStand(gefunden ? 'bereit' : 'nichts');
    } catch {
      setKiStand('nichts');
    }
  }
  const kiKnopf = onKiSuche ? (
    <button className="btn ghost sm" style={{ marginTop: 6 }} onClick={kiSuchen} disabled={kiStand === 'sucht'}>
      {kiStand === 'sucht' ? 'KI sucht im Auszug …' : kiStand === 'nichts' ? 'KI fand nichts — noch einmal suchen' : 'Mit KI im Auszug suchen'}
    </button>
  ) : null;
  // Hat der Abgleich nichts, sucht die Fundstellensuche im Auszugstext —
  // Rechnungsnummer, Betrag, Lieferant — und zeigt die Stelle.
  const ohneTreffer = !befund || befund.status === 'kein_kontoauszug' || (befund.status === 'keine_zahlung' && !(befund.vorschlagIds ?? []).length);
  const [fund, setFund] = useState<Fundstelle | null | 'sucht' | 'nichts'>(null);
  useEffect(() => {
    let verworfen = false;
    setFund(null);
    if (!ohneTreffer || !onFundstelle || !onDokumentStelle) return;
    setFund('sucht');
    onFundstelle(belegId)
      .then((f) => {
        if (!verworfen) setFund(f ?? 'nichts');
      })
      .catch(() => {
        if (!verworfen) setFund('nichts');
      });
    return () => {
      verworfen = true;
    };
  }, [belegId, ohneTreffer, onFundstelle, onDokumentStelle]);

  const fundstelle =
    fund && fund !== 'sucht' && fund !== 'nichts' ? (
      <div className="fundstelle">
        <p className="note" style={{ margin: '6px 0 4px' }}>Im Auszug gefunden — {fund.gruende.join(', ').toLowerCase()}:</p>
        <button
          className="btn ghost sm"
          style={{ justifyContent: 'space-between', width: '100%', textAlign: 'left' }}
          onClick={() => onDokumentStelle!(fund.dokumentId, fund.stelle)}
          title={`${fund.dateiname}, Seite ${fund.stelle.seite}`}
        >
          <span className="fundstelle-text">{fund.text}</span>
          <span className="mono">S. {fund.stelle.seite} →</span>
        </button>
      </div>
    ) : fund === 'sucht' ? (
      <p className="note" style={{ margin: '6px 0 0' }}>Suche im Kontoauszug …</p>
    ) : fund === 'nichts' ? (
      <p className="note" style={{ margin: '6px 0 0' }}>Auch im Text des Auszugs war weder Rechnungsnummer, Betrag noch Lieferant zu finden.</p>
    ) : null;

  if (!befund || befund.status === 'kein_kontoauszug') {
    return (
      <div className="blk">
        <h4>Zahlung</h4>
        <p className="note" style={{ marginTop: 0 }}>
          {befund?.zahldatumLautBuchhaltung
            ? `Laut Buchhaltung bezahlt am ${datum(befund.zahldatumLautBuchhaltung)}. Es ist kein Kontoauszug im Abgleich, der das bestätigen könnte.`
            : 'Es ist kein Kontoauszug im Abgleich. Die Hausverwaltung setzt dafür bei ihrem Kontoauszug den Haken „Für den Belegabgleich".'}
        </p>
        {fundstelle}
        {kiKnopf}
      </div>
    );
  }

  const schild = {
    bezahlt: { klasse: 'ok', text: 'Bezahlt' },
    teilweise: { klasse: 'w', text: 'Teilweise bezahlt' },
    keine_zahlung: { klasse: 'w', text: (befund.vorschlagIds ?? []).length ? 'Zahlung noch zu bestätigen' : 'Keine Zahlung gefunden' },
    datum_weicht_ab: { klasse: 'w', text: 'Zahldatum weicht ab' },
    kein_kontoauszug: { klasse: 'n', text: '' },
  }[befund.status];

  const positionen = befund.transaktionIds
    .map((id) => transaktionen.find((t) => t.id === id))
    .filter((t): t is Banktransaktion => Boolean(t));
  const vorschlaege = (befund.vorschlagIds ?? [])
    .map((id) => transaktionen.find((t) => t.id === id))
    .filter((t): t is Banktransaktion => Boolean(t));

  return (
    <div className="blk">
      <h4>
        Zahlung
        <span className={`pill ${schild.klasse}`} style={{ marginLeft: 8 }}>
          <span className="dot" />
          {schild.text}
        </span>
      </h4>

      <dl className="dl">
        <dt>Laut Buchhaltung</dt>
        <dd>{befund.zahldatumLautBuchhaltung ? datum(befund.zahldatumLautBuchhaltung) : '—'}</dd>
        <dt>Laut Kontoauszug</dt>
        <dd>{befund.zahldatumLautKontoauszug ? datum(befund.zahldatumLautKontoauszug) : '—'}</dd>
        {befund.abweichungTage !== null && befund.abweichungTage !== 0 && (
          <>
            <dt>Unterschied</dt>
            <dd className={befund.status === 'datum_weicht_ab' ? 'warn' : undefined}>
              {Math.abs(befund.abweichungTage)} Tage{' '}
              {befund.abweichungTage > 0 ? 'später' : 'früher'} gebucht
            </dd>
          </>
        )}
        {befund.offen !== null && befund.offen > 0.005 && (
          <>
            <dt>Noch offen</dt>
            <dd className="warn">{eur(befund.offen)}</dd>
          </>
        )}
      </dl>

      {positionen.length > 0 ? (
        <>
          <p className="note" style={{ marginBottom: 6 }}>
            {positionen.length === 1
              ? 'Zugeordnete Position im Kontoauszug:'
              : `${positionen.length} Positionen im Kontoauszug — die Rechnung wurde in Raten oder gesammelt bezahlt:`}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {positionen.map((t) => (
              <PositionsLink key={t.id} t={t} onPosition={onPosition} onDokumentStelle={onDokumentStelle} />
            ))}
          </div>
        </>
      ) : vorschlaege.length > 0 ? (
        <>
          <p className="note" style={{ marginBottom: 6 }}>
            Noch nicht bestätigt — der Abgleich vermutet diese Abbuchung:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {vorschlaege.map((t) => (
              <PositionsLink key={t.id} t={t} onPosition={onPosition} onDokumentStelle={onDokumentStelle} vorschlag />
            ))}
          </div>
        </>
      ) : (
        <>
          <p className="note" style={{ marginTop: 0 }}>
            Der Abgleich fand keine passende Buchung.
          </p>
          {fundstelle}
          {kiKnopf}
        </>
      )}
    </div>
  );
}

/**
 * Eine Kontoauszugsposition im Zahlungsfeld: Datum, Empfänger, Betrag — und
 * der Sprung. Stammt die Buchung aus einem hochgeladenen Auszug, führt der
 * Hauptlink in das PDF an die gelb markierte Stelle; der Reiter
 * „Kontoauszug" bleibt als zweiter Weg.
 */
function PositionsLink({
  t,
  onPosition,
  onDokumentStelle,
  vorschlag,
}: {
  t: Banktransaktion;
  onPosition: (transaktionId: string) => void;
  onDokumentStelle?: (dokumentId: string, stelle: PdfRechteck) => void;
  vorschlag?: boolean;
}) {
  const imPdf = Boolean(t.quelleDokumentId && t.quellePosition && onDokumentStelle);
  return (
    <div className="position-link">
      <button
        className="btn ghost sm"
        style={{ justifyContent: 'space-between', width: '100%', textAlign: 'left' }}
        onClick={() => (imPdf ? onDokumentStelle!(t.quelleDokumentId!, t.quellePosition!) : onPosition(t.id))}
        title={imPdf ? `Im Kontoauszug-PDF anzeigen (Seite ${t.quellePosition!.seite})` : vorschlag ? 'Im Kontoauszug anzeigen und bestätigen' : 'Im Kontoauszug anzeigen'}
      >
        <span>
          {datum(t.buchungsdatum)} · {t.partnerName ?? 'ohne Empfänger'}
        </span>
        <span className="mono">{eur(t.betrag)} →</span>
      </button>
      {imPdf && (
        <div className="position-link-unter">
          <span>Im Auszug, Seite {t.quellePosition!.seite}</span>
          <button className="linkbtn" onClick={() => onPosition(t.id)}>
            {vorschlag ? 'Zuordnung bestätigen' : 'Zur Liste'}
          </button>
        </div>
      )}
    </div>
  );
}

export function Ring({ anteil }: { anteil: number }) {
  const r = 8;
  const umfang = 2 * Math.PI * r;
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden style={{ flex: '0 0 auto' }}>
      <circle cx="11" cy="11" r={r} fill="none" stroke="var(--line)" strokeWidth="3" />
      <circle
        cx="11"
        cy="11"
        r={r}
        fill="none"
        stroke={anteil >= 1 ? 'var(--ok)' : 'var(--pri)'}
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={`${(umfang * anteil).toFixed(1)} ${umfang.toFixed(1)}`}
        transform="rotate(-90 11 11)"
      />
    </svg>
  );
}
